import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { assertPagePermission } from "@/features/auth/lib/assert-page-permission";
import {
  adminOrdersSummaryQueryOptions,
  pageStateQueryOptions,
  PageAccessError,
} from "@/lib/queries";
import { PageError } from "@/features/auth/components/PageError";
import { usePageState } from "@/hooks/usePageState";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { OrdersDashboardPage } from "@/features/marketplace/admin/pages/OrdersDashboardPage";

function MarketplaceOrdersRoute() {
  const { t } = useTranslation();
  const { orderId } = Route.useSearch();
  useDocumentTitle(t("marketplace.ordersDashboard.title", "Orders tracker"));

  const { isLoading, error } = usePageState("marketplace.orders");

  if (isLoading) return null;
  if (error) return <PageError is403={error instanceof PageAccessError} />;
  return <OrdersDashboardPage initialOrderId={orderId} />;
}

export const Route = createFileRoute("/_authenticated/marketplace/orders")({
  validateSearch: z.object({
    orderId: z.string().trim().min(1).max(100).optional(),
  }),
  beforeLoad: () => assertPagePermission("marketplace.orders"),
  loader: ({ context }) => {
    void context.queryClient.prefetchQuery(adminOrdersSummaryQueryOptions());
    return context.queryClient
      .ensureQueryData(pageStateQueryOptions("marketplace.orders"))
      .catch((err: unknown) => {
        if (err instanceof PageAccessError) return null;
        throw err;
      });
  },
  component: MarketplaceOrdersRoute,
  errorComponent: ({ error }) => (
    <PageError is403={error instanceof PageAccessError} />
  ),
});
